import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { trackEvent, getTrackingMeta, trackFormStart, trackQuoteSubmit, trackEcommerceQuoteSubmitted, SERVICE } from "@/lib/tracking";
import { ACCENT } from "@/components/ecommerce/shared";
import { Progress, FormIntro, FormSuccess } from "@/components/ecommerce/EcomFormParts";
import { StepNeed, StepBusiness, StepContact, SERVICE_CHOICES } from "@/components/ecommerce/EcomFormSteps";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STEPS = ["What you need", "Your business", "Contact details"];
const EMPTY = {
  service: "", marketplace: "", company_name: "", website_url: "", channels: [], products: "", challenge: "", timeframe: "",
  first_name: "", last_name: "", business_email: "", mobile_number: "", message: "", privacy: false, marketing: false, hp: "",
};

const readPreset = () => {
  try { const v = sessionStorage.getItem("rekisa_ecom_service"); return SERVICE_CHOICES.includes(v) ? v : ""; } catch (_) { return ""; }
};

function stepError(step, f) {
  if (step === 0) {
    if (!f.service) return "Please choose what you need.";
    if (f.service === "Marketplace Enablement" && !f.marketplace) return "Please choose a marketplace.";
  }
  if (step === 1) {
    if (!f.company_name.trim()) return "Business name is required.";
    if (f.website_url.trim() && !/^(https?:\/\/)?[^\s.]+\.[^\s]{2,}$/.test(f.website_url.trim())) return "Please enter a valid website address or leave it blank.";
    if (!f.channels.length) return "Please choose at least one current sales channel.";
    if (!f.products) return "Please choose an approximate number of products.";
    if (!f.challenge) return "Please choose your main eCommerce challenge.";
    if (!f.timeframe) return "Please choose an implementation timeframe.";
  }
  if (step === 2) {
    if (!f.first_name.trim()) return "First name is required.";
    if (!f.last_name.trim()) return "Last name is required.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(f.business_email.trim())) return "A valid business email is required.";
    const digits = f.mobile_number.replace(/\D/g, "");
    if (digits.length < 9 || digits.length > 15) return "Please enter a valid mobile number, e.g. 071 234 5678.";
    if (!f.privacy) return "Please accept the privacy notice so we can respond to your enquiry.";
  }
  return null;
}

const buildPayload = (f) => ({
  service_type: SERVICE.ecommerce,
  what_to_build: [f.service],
  marketplaces: f.service === "Marketplace Enablement" && f.marketplace ? [f.marketplace] : [],
  company_name: f.company_name.trim(),
  website_url: f.website_url.trim() || null,
  enquiry_channels: f.channels,
  product_count: f.products,
  problem_to_solve: f.challenge,
  go_live_timing: f.timeframe,
  support_option: "not_sure",
  first_name: f.first_name.trim(),
  last_name: f.last_name.trim(),
  business_email: f.business_email.trim(),
  mobile_number: f.mobile_number.trim(),
  message: f.message.trim() || null,
  privacy_consent: f.privacy,
  marketing_consent: f.marketing,
  hp: f.hp || null,
  tracking: getTrackingMeta(),
});

export default function EcomForm() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(() => ({ ...EMPTY, service: readPreset() }));
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const startedRef = useRef(false);
  const inFlightRef = useRef(false);

  useEffect(() => {
    const onPreset = (e) => {
      if (!SERVICE_CHOICES.includes(e.detail)) return;
      setForm((f) => ({ ...f, service: e.detail }));
      setStep(0);
      setError("");
    };
    window.addEventListener("rekisa:ecom-service", onPreset);
    return () => window.removeEventListener("rekisa:ecom-service", onPreset);
  }, []);

  const set = (k, v) => {
    if (!startedRef.current) {
      startedRef.current = true;
      trackFormStart({ service: SERVICE.ecommerce, formName: "ecommerce_plan_form" });
    }
    setForm((f) => ({ ...f, [k]: v }));
    setError("");
  };
  const toggle = (k, v) => set(k, form[k].includes(v) ? form[k].filter((x) => x !== v) : [...form[k], v]);

  const scrollTop = () => requestAnimationFrame(() => document.getElementById("request-quote")?.scrollIntoView({ block: "start" }));
  const fail = (msg) => { setError(msg); toast.error(msg); };
  const next = () => {
    const err = stepError(step, form);
    if (err) return fail(err);
    trackEvent("form_step", { service: SERVICE.ecommerce, step: step + 1 });
    setStep(step + 1);
    scrollTop();
  };
  const back = () => { setError(""); setStep(step - 1); scrollTop(); };

  const submit = async (e) => {
    e.preventDefault();
    if (inFlightRef.current || status === "done") return;
    const err = stepError(2, form);
    if (err) return fail(err);
    inFlightRef.current = true;
    setStatus("sending");
    try {
      const res = await axios.post(`${API}/quote`, buildPayload(form));
      const marketplaces = res.data?.marketplaces || [];
      trackEcommerceQuoteSubmitted({ leadId: res.data?.id, serviceInterest: form.service, marketplaces, timeframe: form.timeframe });
      trackQuoteSubmit({ service: SERVICE.ecommerce, planId: form.service, goLive: form.timeframe });
      toast.dismiss();
      setStatus("done");
      scrollTop();
    } catch (err2) {
      if (process.env.NODE_ENV !== "production") console.error(err2);
      setStatus("idle");
      fail("Could not submit your enquiry. Please try again.");
    } finally {
      inFlightRef.current = false;
    }
  };

  return (
    <section id="request-quote" aria-labelledby="request-quote-heading" data-testid="ecom-request-quote-section" className="relative py-20 sm:py-28 bg-[#16120F] text-white">
      {status === "done" ? <FormSuccess /> : (
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <FormIntro />
          <Progress steps={STEPS} step={step} />
          <form onSubmit={submit} noValidate data-testid="ecom-form" className="relative rounded-3xl border border-white/10 bg-white/[0.03] p-6 sm:p-9">
            <h3 className="font-display text-xl font-black mb-6">{["What do you need?", "Your business", "Your contact details"][step]}</h3>
            {step === 0 && <StepNeed form={form} set={set} />}
            {step === 1 && <StepBusiness form={form} set={set} toggle={toggle} />}
            {step === 2 && <StepContact form={form} set={set} />}
            {error && <p role="alert" data-testid="ecom-form-error" className="mt-6 rounded-xl border border-rose-400/40 bg-rose-500/10 px-4 py-2.5 text-sm text-rose-200">{error}</p>}
            <FormNav step={step} sending={status === "sending"} onBack={back} onNext={next} />
          </form>
        </div>
      )}
    </section>
  );
}

function FormNav({ step, sending, onBack, onNext }) {
  const btn = "ml-auto rounded-full whitespace-normal h-auto min-h-12 px-7 py-3 text-base font-bold text-white transition-[filter,opacity] hover:brightness-110 disabled:opacity-70";
  return (
    <div className="mt-8 flex flex-wrap items-center gap-3">
      {step > 0 && (
        <button type="button" onClick={onBack} data-testid="ecom-form-back" className="inline-flex items-center gap-1.5 rounded-full min-h-12 px-5 py-3 text-sm font-semibold text-white/75 border border-white/15 hover:bg-white/[0.06] transition-colors">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
      )}
      {step < 2 ? (
        <Button key="ecom-next" type="button" onClick={onNext} data-testid="ecom-form-next" className={btn} style={{ background: ACCENT }}>
          Continue <ArrowRight className="ml-1 h-5 w-5" />
        </Button>
      ) : (
        <Button key="ecom-submit" type="submit" disabled={sending} data-testid="ecom-form-submit" className={btn} style={{ background: ACCENT }}>
          {sending ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Submitting...</> : <>Get My eCommerce Plan <ArrowRight className="ml-1 h-5 w-5" /></>}
        </Button>
      )}
    </div>
  );
}
